/**
 * 施術録検索 list hook — clinic-wide rezept search.
 * Pass `null` as payload to skip the query (e.g. before defaults computed).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import { searchTreatmentRecords } from '@/services/main/treatmentRecord';
import type {
  TreatmentRecordSearchPayload,
  TreatmentRecordSearchResult,
} from '@/pages/main/treatmentRecord/types';

export const useTreatmentRecordSearch = (payload: TreatmentRecordSearchPayload | null) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    TreatmentRecordSearchResult,
    Error
  >({
    queryKey: ['treatment-records', 'search', tenant, clinicNumber, serviceType, payload],
    queryFn: () => searchTreatmentRecords(tenant, clinicNumber, payload!, serviceType),
    enabled: !!tenant && !!clinicNumber && payload !== null,
    // Keep previous rows during pagination/filter changes for smooth UX, but drop
    // them when the user switches service type (A2 ↔ R2) — stale A2 rows must not
    // flash under R2 results while the new fetch is in-flight.
    placeholderData: (prev, prevQuery) => {
      if (!prev || !prevQuery) return undefined;
      const prevServiceType = (prevQuery.queryKey as unknown[])[4];
      return prevServiceType === serviceType ? prev : undefined;
    },
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading: isLoading || isFetching, refetch };
};
